import { useState, useEffect } from 'react';
import { AdminShell } from '@/components/AdminShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Save, Shield, User } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function AdminSettings() {
  const [profile, setProfile] = useState({ name: '', email: '' });
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/admin/me').then(res => {
      setProfile({ name: res.data.name || '', email: res.data.email || '' });
    }).catch(console.error);
  }, []);

  const handleProfileSave = async () => {
    try {
      const res = await api.put('/admin/profile', profile);
      toast.success('Profile updated.');
      setProfile({ name: res.data.name || '', email: res.data.email || '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile.');
    }
  };

  const handlePasswordChange = async () => {
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('New passwords do not match.');
      return;
    }
    if (passwords.new_password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      await api.put('/admin/password', {
        current_password: passwords.current_password,
        new_password: passwords.new_password
      });
      toast.success('Password changed successfully.');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminShell title="Settings">
      <div className="max-w-2xl space-y-8">
        {/* Profile */}
        <Card className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.15)]">
          <CardHeader>
            <CardTitle className="font-['Cormorant_Garamond'] text-xl text-[hsl(var(--foreground))] flex items-center gap-3">
              <User className="h-5 w-5 text-[hsl(var(--gold))]" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">
                Name
              </label>
              <Input
                value={profile.name}
                onChange={e => setProfile({ ...profile, name: e.target.value })}
                className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm"
              />
            </div>
            <div>
              <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">
                Email
              </label>
              <Input
                type="email"
                value={profile.email}
                onChange={e => setProfile({ ...profile, email: e.target.value })}
                className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm"
              />
            </div>
            <Button
              onClick={handleProfileSave}
              className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium px-6 py-5 rounded-lg"
            >
              <Save className="h-4 w-4 mr-2" /> Save Profile
            </Button>
          </CardContent>
        </Card>

        {/* Password */}
        <Card className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.15)]">
          <CardHeader>
            <CardTitle className="font-['Cormorant_Garamond'] text-xl text-[hsl(var(--foreground))] flex items-center gap-3">
              <Shield className="h-5 w-5 text-[hsl(var(--gold))]" />
              Change Password
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">
                Current Password
              </label>
              <Input
                type="password"
                value={passwords.current_password}
                onChange={e => setPasswords({ ...passwords, current_password: e.target.value })}
                className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm"
              />
            </div>
            <div>
              <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">
                New Password
              </label>
              <Input
                type="password"
                value={passwords.new_password}
                onChange={e => setPasswords({ ...passwords, new_password: e.target.value })}
                className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm"
              />
            </div>
            <div>
              <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">
                Confirm New Password
              </label>
              <Input
                type="password"
                value={passwords.confirm_password}
                onChange={e => setPasswords({ ...passwords, confirm_password: e.target.value })}
                className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm"
              />
            </div>
            <Button
              onClick={handlePasswordChange}
              disabled={loading || !passwords.current_password || !passwords.new_password}
              className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium px-6 py-5 rounded-lg disabled:opacity-50"
            >
              <Shield className="h-4 w-4 mr-2" /> {loading ? 'Updating...' : 'Change Password'}
            </Button>
          </CardContent>
        </Card>

        {/* Info */}
        <Card className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.15)]">
          <CardContent className="pt-6">
            <p className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-4">
              System Information
            </p>
            <div className="space-y-2 font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">
              <p>SendGrid Sender: <span className="text-[hsl(var(--foreground))]">noreply@thecosmicsynthesist.com</span></p>
              <p>Stripe: <span className="text-[hsl(var(--sage))]">Connected (Live)</span></p>
              <p>Database: <span className="text-[hsl(var(--sage))]">MongoDB Atlas</span></p>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminShell>
  );
}
